import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { protestService, governorateService, categoryService, adminService } from '../services/api';
import styles from './EditProtestForm.module.css';

const PROTEST_TYPES = ['مظاهرة', 'إضراب', 'اعتصام', 'وقفة احتجاجية', 'عريضة', 'أخرى'];

const POLITICAL_ORIENTATIONS = [
    { value: '',                    label: '— اختر التوجه —' },
    { value: 'government_supporter', label: 'مؤيد للحكومة' },
    { value: 'government_opponent',  label: 'معارض للحكومة' },
    { value: 'sectarian',            label: 'طائفي' },
    { value: 'separatist',           label: 'انفصالي' },
    { value: 'neutral_undefined',    label: 'محايد / غير محدد' },
    { value: 'riots',                label: 'أعمال شغب' },
];

export default function EditProtestForm({ protest, onSave, onCancel }) {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        governorate_id:         protest.governorate_id       ?? '',
        region_id:              protest.region_id            ?? '',
        protest_date:           (protest.protest_date        ?? '').split('T')[0],
        protest_type:           protest.protest_type         ?? 'مظاهرة',
        estimated_participants: protest.estimated_participants ?? '',
        demands:                protest.demands              ?? '',
        category_id:            protest.category_id          ?? '',
        sub_category_id:        protest.sub_category_id      ?? '',
        verified:               protest.verified             ?? false,
        political_orientation:  protest.political_orientation ?? '',
        source_url:             protest.source_url           ?? '',
    });

    const [governorates, setGovernorates] = useState([]);
    const [regions,      setRegions]      = useState([]);
    const [categories,   setCategories]   = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [saving,        setSaving]       = useState(false);
    const [error,         setError]        = useState('');
    const [addingRegion,  setAddingRegion] = useState(false);
    const [newRegionName, setNewRegionName] = useState('');
    const [savingRegion,  setSavingRegion] = useState(false);

    useEffect(() => {
        governorateService.list().then((r) => setGovernorates(r.data)).catch(console.error);
        categoryService.list().then((r) => setCategories(r.data)).catch(console.error);
    }, []);

    useEffect(() => {
        if (!form.governorate_id) { setRegions([]); return; }
        governorateService.regions(form.governorate_id)
            .then((r) => setRegions(r.data))
            .catch(console.error);
    }, [form.governorate_id]);

    useEffect(() => {
        const cat = categories.find((c) => c.id === Number(form.category_id));
        setSubCategories(cat?.sub_categories ?? []);
        if (!cat?.sub_categories?.find((s) => s.id === Number(form.sub_category_id))) {
            setForm((f) => ({ ...f, sub_category_id: '' }));
        }
    }, [form.category_id, categories]);

    const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

    const handleAddRegion = async () => {
        if (!newRegionName.trim() || !form.governorate_id) return;
        setSavingRegion(true);
        try {
            const res = await adminService.storeRegion({ name: newRegionName.trim(), governorate_id: Number(form.governorate_id) });
            const created = res.data;
            setRegions((prev) => [...prev, created]);
            set('region_id', String(created.id));
            setNewRegionName('');
            setAddingRegion(false);
        } catch (err) {
            setError(err.response?.data?.message || 'فشل إضافة المنطقة');
        } finally {
            setSavingRegion(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const payload = {
                ...form,
                governorate_id:         form.governorate_id         || null,
                region_id:              form.region_id              || null,
                category_id:            form.category_id            || null,
                sub_category_id:        form.sub_category_id        || null,
                estimated_participants: form.estimated_participants !== '' ? Number(form.estimated_participants) : null,
                political_orientation:  form.political_orientation  || null,
                source_url:             form.source_url             || null,
            };
            const res = await protestService.update(protest.id, payload);
            onSave(res.data);
        } catch (err) {
            setError(err.response?.data?.message || 'فشل الحفظ');
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className={`${styles.form} text-ar`}>
            <div className={styles.formHeader}>
                <h2 className={styles.title}>تعديل الاحتجاج</h2>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        className={styles.fullEditBtn}
                        onClick={() => navigate(`/protests/${protest.id}`)}
                        title="فتح صفحة التعديل الكاملة"
                    >
                        ↗ التعديل الشامل
                    </button>
                    <button type="button" className={styles.cancelTop} onClick={onCancel}>✕</button>
                </div>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.grid}>
                {/* Governorate */}
                <div className={styles.field}>
                    <label className={styles.label}>المحافظة</label>
                    <select className={styles.select} value={form.governorate_id}
                        onChange={(e) => { set('governorate_id', e.target.value); set('region_id', ''); }}>
                        <option value="">— اختر محافظة —</option>
                        {governorates.map((g) => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                    </select>
                </div>

                {/* Region */}
                <div className={styles.field}>
                    <label className={styles.label}>المنطقة</label>
                    {addingRegion ? (
                        <div className={styles.inlineAdd}>
                            <input
                                className={styles.input}
                                value={newRegionName}
                                onChange={(e) => setNewRegionName(e.target.value)}
                                placeholder="اسم المنطقة الجديدة"
                                autoFocus
                                onKeyDown={(e) => e.key === 'Enter' && handleAddRegion()}
                            />
                            <button type="button" className={styles.confirmBtn} onClick={handleAddRegion} disabled={savingRegion}>
                                {savingRegion ? '...' : '+'}
                            </button>
                            <button type="button" className={styles.xBtn} onClick={() => { setAddingRegion(false); setNewRegionName(''); }}>×</button>
                        </div>
                    ) : (
                        <div className={styles.inlineAdd}>
                            <select className={`${styles.select} ${styles.flex1}`} value={form.region_id}
                                onChange={(e) => set('region_id', e.target.value)}
                                disabled={!form.governorate_id}>
                                <option value="">— اختر منطقة —</option>
                                {regions.map((r) => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </select>
                            <button type="button" className={styles.newBtn} onClick={() => setAddingRegion(true)} disabled={!form.governorate_id} title="إضافة منطقة جديدة">+</button>
                        </div>
                    )}
                </div>

                {/* Date */}
                <div className={styles.field}>
                    <label className={styles.label}>التاريخ</label>
                    <input className={styles.input} type="date" value={form.protest_date}
                        onChange={(e) => set('protest_date', e.target.value)} />
                </div>

                {/* Type */}
                <div className={styles.field}>
                    <label className={styles.label}>النوع</label>
                    <select className={styles.select} value={form.protest_type}
                        onChange={(e) => set('protest_type', e.target.value)}>
                        {PROTEST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>

                {/* Participants */}
                <div className={styles.field}>
                    <label className={styles.label}>عدد المشاركين</label>
                    <input className={styles.input} type="number" min="0" value={form.estimated_participants}
                        onChange={(e) => set('estimated_participants', e.target.value)} />
                </div>

                {/* Category */}
                <div className={styles.field}>
                    <label className={styles.label}>التصنيف</label>
                    <select className={styles.select} value={form.category_id}
                        onChange={(e) => set('category_id', e.target.value)}>
                        <option value="">— بدون تصنيف —</option>
                        {categories.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                {/* Sub-category */}
                <div className={styles.field}>
                    <label className={styles.label}>التصنيف الفرعي</label>
                    <select className={styles.select} value={form.sub_category_id}
                        onChange={(e) => set('sub_category_id', e.target.value)}
                        disabled={!form.category_id || !subCategories.length}>
                        <option value="">— بدون تصنيف فرعي —</option>
                        {subCategories.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Demands */}
            <div className={styles.field}>
                <label className={styles.label}>المطالب</label>
                <textarea className={styles.textarea} rows={3} value={form.demands}
                    onChange={(e) => set('demands', e.target.value)} />
            </div>

            <div className={styles.grid}>
                {/* Political Orientation */}
                <div className={styles.field}>
                    <label className={styles.label}>التوجه السياسي</label>
                    <select className={styles.select} value={form.political_orientation}
                        onChange={(e) => set('political_orientation', e.target.value)}>
                        {POLITICAL_ORIENTATIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>

                {/* Source URL */}
                <div className={styles.field}>
                    <label className={styles.label}>رابط المصدر</label>
                    <input className={styles.input} type="url" value={form.source_url}
                        onChange={(e) => set('source_url', e.target.value)} placeholder="https://..." />
                </div>
            </div>

            {/* Verified */}
            <div className={styles.field}>
                <label className={styles.checkLabel}>
                    <input type="checkbox" checked={!!form.verified}
                        onChange={(e) => set('verified', e.target.checked)} />
                    <span>مرصود ميدانياً (موثق)</span>
                </label>
            </div>

            <div className={styles.actions}>
                <button type="submit" className={styles.saveBtn} disabled={saving}>
                    {saving ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                </button>
                <button type="button" className={styles.cancelBtn} onClick={onCancel}>إلغاء</button>
            </div>
        </form>
    );
}
